import { throwApiError, unwrapApiData } from '@/shared/utils/apiResponse';
import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import {
  CreateTemplateInput,
  ModeloPortal,
  Template,
  TemplateListResponse,
  TemplatePadraoPreview,
  UpdateTemplateInput,
  UploadLogoResponse,
} from '../types';

const BASE_URL = '/extrajudicial/templates';
const ERRO_PREVIEW_PADRAO = 'Erro ao carregar o template padrão';
const ERRO_MODELO_PORTAL = 'Erro ao carregar a pré-visualização do template';

export const templateService = {
  async list(params?: {
    nome?: string;
    status?: string;
    tipo?: string;
    page?: number;
    pageSize?: number;
  }): Promise<TemplateListResponse> {
    const queryParams: Record<string, string | number | boolean> = {};
    if (params?.nome) queryParams.nome = params.nome;
    if (params?.status) queryParams.status = params.status;
    if (params?.tipo) queryParams.tipo = params.tipo;
    if (params?.page) queryParams.page = params.page;
    if (params?.pageSize) queryParams.pageSize = params.pageSize;

    const response = await apiGet(BASE_URL, queryParams);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao listar templates');
    }
    return response.json();
  },

  async getById(id: string): Promise<Template> {
    const response = await apiGet(`${BASE_URL}/${id}`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao buscar template');
    }
    return response.json();
  },

  async create(data: CreateTemplateInput): Promise<{ success: boolean; data: Template }> {
    const response = await apiPost(BASE_URL, data);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao criar template');
    }
    return response.json();
  },

  async update(
    id: string,
    data: UpdateTemplateInput,
  ): Promise<{ success: boolean; data: Template }> {
    const response = await apiPut(`${BASE_URL}/${id}`, data);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao atualizar template');
    }
    return response.json();
  },

  async publish(id: string): Promise<{ success: boolean; data: Template }> {
    const response = await apiPost(`${BASE_URL}/${id}/publish`, {});
    if (!response.ok) {
      await throwApiError(response, 'Erro ao publicar template');
    }
    return response.json();
  },

  async delete(id: string): Promise<{ success: boolean }> {
    const response = await apiDelete(`${BASE_URL}/${id}`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao excluir template');
    }
    return response.json();
  },

  async checkName(params: {
    nome: string;
    canal: string;
    excludeId?: string;
  }): Promise<{ available: boolean }> {
    const queryParams: Record<string, string> = {
      nome: params.nome,
      canal: params.canal,
    };
    if (params.excludeId) queryParams.excludeId = params.excludeId;

    const response = await apiGet(`${BASE_URL}/check-name`, queryParams);
    if (!response.ok) {
      return { available: true };
    }
    const result: { success: boolean; data: { available: boolean } } = await response.json();
    return result.data;
  },

  async getPreviewPadraoArbitralis(): Promise<string> {
    const response = await apiGet(`${BASE_URL}/padrao-arbitralis/preview`);
    const { html } = await unwrapApiData<TemplatePadraoPreview>(response, {
      fallback: ERRO_PREVIEW_PADRAO,
      source: 'templateService.getPreviewPadraoArbitralis',
    });
    if (!html) throw new Error(ERRO_PREVIEW_PADRAO);
    return html;
  },

  async getModeloPortal(): Promise<string> {
    const response = await apiGet(`${BASE_URL}/portal/modelo`);
    const { html } = await unwrapApiData<ModeloPortal>(response, {
      fallback: ERRO_MODELO_PORTAL,
      source: 'templateService.getModeloPortal',
    });
    if (!html) throw new Error(ERRO_MODELO_PORTAL);
    return html;
  },

  async uploadLogo(file: File): Promise<UploadLogoResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiPost('/upload/logo', formData);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao fazer upload do logo');
    }
    return response.json();
  },
};
